import React, { useState, useMemo } from 'react';
import ClassificationBadge from './ClassificationBadge';
import {
  ArrowsUpDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// Confidence tier definitions
const CONFIDENCE_TIERS = [
  { label: 'Low', min: 0, max: 0.5, borderColor: '#ef4444', bgColor: 'rgba(127, 29, 29, 0.3)' },
  { label: 'Moderate', min: 0.5, max: 0.7, borderColor: '#f59e0b', bgColor: 'rgba(120, 53, 15, 0.3)' },
  { label: 'Good', min: 0.7, max: 0.85, borderColor: '#eab308', bgColor: 'rgba(113, 63, 18, 0.3)' },
  { label: 'High', min: 0.85, max: 1.01, borderColor: '#22c55e', bgColor: 'rgba(20, 83, 45, 0.3)' },
];

function getTier(confidence) {
  const val = confidence ?? 0;
  for (const tier of CONFIDENCE_TIERS) {
    if (val >= tier.min && val < tier.max) return tier;
  }
  return CONFIDENCE_TIERS[CONFIDENCE_TIERS.length - 1];
}

// Sort options
const SORT_OPTIONS = [
  { value: 'paragraph', label: 'Paragraph Order' },
  { value: 'confidence_asc', label: 'Confidence (Low to High)' },
  { value: 'confidence_desc', label: 'Confidence (High to Low)' },
];

export default function ConfidenceHeatmap({ paragraphResult }) {
  const [sortBy, setSortBy] = useState('paragraph');

  const paragraphs = paragraphResult?.paragraphs || [];

  // Compute summary statistics
  const stats = useMemo(() => {
    if (paragraphs.length === 0) return null;
    const confidences = paragraphs.map(p => p.confidence ?? 0);
    const sum = confidences.reduce((a, b) => a + b, 0);
    const avg = sum / confidences.length;
    const min = Math.min(...confidences);
    const max = Math.max(...confidences);
    const belowThreshold = confidences.filter(c => c < 0.7).length;
    return { avg, min, max, belowThreshold, total: confidences.length };
  }, [paragraphs]);

  // Sorted paragraphs
  const sortedParagraphs = useMemo(() => {
    const copy = [...paragraphs];
    if (sortBy === 'confidence_asc') {
      copy.sort((a, b) => (a.confidence ?? 0) - (b.confidence ?? 0));
    } else if (sortBy === 'confidence_desc') {
      copy.sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
    }
    // 'paragraph' keeps original order
    return copy;
  }, [paragraphs, sortBy]);

  if (!paragraphResult || paragraphs.length === 0) return null;

  return (
    <div className="space-y-4">
      {/* Summary Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="vault-card p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1 font-mono">
              Avg Confidence
            </div>
            <div className="text-xl font-bold font-mono" style={{ color: getTier(stats.avg).borderColor }}>
              {(stats.avg * 100).toFixed(1)}%
            </div>
          </div>
          <div className="vault-card p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1 font-mono">
              Min Confidence
            </div>
            <div className="text-xl font-bold font-mono" style={{ color: getTier(stats.min).borderColor }}>
              {(stats.min * 100).toFixed(1)}%
            </div>
          </div>
          <div className="vault-card p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1 font-mono">
              Max Confidence
            </div>
            <div className="text-xl font-bold font-mono" style={{ color: getTier(stats.max).borderColor }}>
              {(stats.max * 100).toFixed(1)}%
            </div>
          </div>
          <div className="vault-card p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1 font-mono">
              Below 70%
            </div>
            <div className="text-xl font-bold font-mono" style={{
              color: stats.belowThreshold > 0 ? '#f59e0b' : '#22c55e'
            }}>
              {stats.belowThreshold}
              <span className="text-xs text-[var(--text-muted)] ml-1">/ {stats.total}</span>
            </div>
          </div>
        </div>
      )}

      {/* Color Legend + Sort Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* Legend */}
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-mono">Scale:</span>
          {CONFIDENCE_TIERS.map(tier => (
            <div key={tier.label} className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: tier.borderColor }}
              />
              <span className="text-xs text-[var(--text-secondary)]">
                {tier.label} ({Math.round(tier.min * 100)}-{Math.round((tier.max >= 1.01 ? 1.0 : tier.max) * 100)}%)
              </span>
            </div>
          ))}
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <ArrowsUpDownIcon className="w-3.5 h-3.5 text-[var(--text-muted)]" />
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="text-xs"
            style={{
              background: 'var(--vault-bg)',
              border: '1px solid var(--vault-border)',
              color: 'var(--text-secondary)',
              padding: '4px 28px 4px 8px',
              borderRadius: '2px',
              fontSize: '12px',
            }}
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Confidence Distribution Bar (mini visualization) */}
      <div className="vault-card p-3">
        <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-2 font-mono">
          Confidence Distribution
        </div>
        <div className="flex gap-0.5 h-8 items-end">
          {paragraphs.map((p, i) => {
            const conf = p.confidence ?? 0;
            const tier = getTier(conf);
            return (
              <div
                key={i}
                title={`P${p.paragraph_number || i + 1}: ${(conf * 100).toFixed(0)}% - ${p.classification || 'UNKNOWN'}`}
                className="flex-1 rounded-t-sm transition-all hover:opacity-80 cursor-default"
                style={{
                  backgroundColor: tier.borderColor,
                  height: `${Math.max(conf * 100, 5)}%`,
                  minWidth: '4px',
                  maxWidth: '40px',
                }}
              />
            );
          })}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[9px] text-[var(--text-muted)] font-mono">P1</span>
          <span className="text-[9px] text-[var(--text-muted)] font-mono">P{paragraphs.length}</span>
        </div>
      </div>

      {/* Paragraph Cards */}
      <div className="space-y-2">
        {sortedParagraphs.map((paragraph, index) => {
          const confidence = paragraph.confidence ?? 0;
          const tier = getTier(confidence);
          const textPreview = (paragraph.text || '').length > 100
            ? (paragraph.text || '').substring(0, 100) + '...'
            : (paragraph.text || '');

          return (
            <div
              key={paragraph.paragraph_number || index}
              className="vault-card overflow-hidden transition-all hover:shadow-lg"
              style={{
                borderLeft: `4px solid ${tier.borderColor}`,
                background: tier.bgColor,
              }}
            >
              <div className="p-3 flex items-start gap-3">
                {/* Paragraph number */}
                <div
                  className="flex-shrink-0 w-8 h-8 rounded flex items-center justify-center text-xs font-bold font-mono"
                  style={{
                    background: 'var(--vault-bg)',
                    border: `1px solid ${tier.borderColor}`,
                    color: tier.borderColor,
                  }}
                >
                  {paragraph.paragraph_number || index + 1}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <ClassificationBadge
                      classification={paragraph.classification}
                      fullClassification={paragraph.full_classification}
                      caveats={paragraph.caveats}
                      releasability={paragraph.releasability}
                      size="small"
                    />
                    {confidence < 0.5 && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#ef4444] font-mono uppercase tracking-wider">
                        <ExclamationTriangleIcon className="w-3 h-3" />
                        Low Confidence
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] leading-relaxed font-mono truncate">
                    {textPreview}
                  </p>
                </div>

                {/* Confidence percentage - large and prominent */}
                <div className="flex-shrink-0 text-right">
                  <div
                    className="text-2xl font-bold font-mono tabular-nums"
                    style={{ color: tier.borderColor }}
                  >
                    {(confidence * 100).toFixed(0)}%
                  </div>
                  <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)] font-mono">
                    {tier.label}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
